import React, { useState, useEffect, useCallback } from 'react';
import './TestRuns.css';
import { ShimmerTableRow } from '../Shimmer';
import CustomDropdown from '../CustomDropdown';
import AlertModal from '../AlertModal';
import ConfirmModal from '../ConfirmModal';
import { API_BASE_URL } from '../../utils/api';
import { waitForExecution } from '../../utils/execution';

function TestRuns({ selectedApplication, ws, isConnected }) {
  const [testRuns, setTestRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 342, totalPages: 18 });
  const [statusFilter, setStatusFilter] = useState('All Status');
  // Feeds the time-range dropdown, which is currently commented out below.
  // eslint-disable-next-line no-unused-vars
  const [timeFilter, setTimeFilter] = useState('Last 7 days');
  const [selectedRun, setSelectedRun] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [alertModal, setAlertModal] = useState({ isOpen: false, message: '', type: 'info' });
  const [rerunningIds, setRerunningIds] = useState(new Set());
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, message: '', onConfirm: null });
  // AI failure explanation: keyed by run id so a reopened row shows instantly.
  const [explainModal, setExplainModal] = useState({ isOpen: false, run: null, loading: false, text: '', reason: '' });

  // Summary stats - fetched from backend
  const [summaryStats, setSummaryStats] = useState({
    totalRuns: 0,
    successRate: 0,
    passed: 0,
    failed: 0,
    avgDuration: 0
  });

  const loadTestRuns = useCallback(async () => {
    setLoading(true);
    const startTime = Date.now();
    const minLoadingTime = 2000;
    
    try {
      if (!selectedApplication) {
        setTestRuns([]);
        setLoading(false);
        return;
      }
      
      const params = new URLSearchParams({
        page: currentPage,
        limit: 10,
        application_id: selectedApplication.id
      });
      if (statusFilter && statusFilter !== 'All Status') {
        params.append('status', statusFilter.toLowerCase());
      }
      
      const response = await fetch(`${API_BASE_URL}/api/test-runs?${params}`);
      const data = await response.json();
      
      console.log('Test runs API response:', data);
      
      if (data.success) {
        const runs = data.testRuns || [];
        console.log('Loaded test runs:', runs);
        console.log('Sample run with trace_file_path:', runs.find(r => r.trace_file_path));
        setTestRuns(runs);
        setPagination(data.pagination || { page: 1, limit: 10, total: 342, totalPages: 18 });
      }
    } catch (error) {
      console.error('Error loading test runs:', error);
     // Use mock data for now
  
    } finally {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minLoadingTime - elapsed);
      setTimeout(() => {
        setLoading(false);
      }, remaining);
    }
  }, [currentPage, statusFilter, selectedApplication]);

  const loadStatistics = async () => {
    try {
      if (!selectedApplication) {
        setSummaryStats({ totalRuns: 0, successRate: 0, passed: 0, failed: 0, avgDuration: 0 });
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/statistics/test-runs?application_id=${selectedApplication.id}&days=30`);
      const data = await response.json();
      if (data.success && data.stats) {
        setSummaryStats({
          totalRuns: data.stats.totalRuns || 0,
          successRate: data.stats.successRate || 0,
          passed: data.stats.passed || 0,
          failed: data.stats.failed || 0,
          avgDuration: data.stats.avgDuration || 0
        });
      } else {
        setSummaryStats({ totalRuns: 0, successRate: 0, passed: 0, failed: 0, avgDuration: 0 });
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
      setSummaryStats({ totalRuns: 0, successRate: 0, passed: 0, failed: 0, avgDuration: 0 });
    }
  };

  useEffect(() => {
    if (selectedApplication) {
      loadTestRuns();
      loadStatistics();
    }
    // Deliberate: the load* helpers are rebuilt on every render, so listing
    // them here would fetch -> setState -> render -> fetch forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadTestRuns, selectedApplication]);

  // Listen for test execution completion to refresh test runs
  useEffect(() => {
    if (!ws || !isConnected) return;

    const handleMessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'testExecutionCompleted' || data.type === 'testFinished') {
          // Refresh test runs when a test execution completes
          console.log('Test execution completed, refreshing test runs...', data.type);
          // Delay to ensure database is updated
          setTimeout(() => {
            if (selectedApplication) {
              loadTestRuns();
              loadStatistics(); // Also refresh statistics
            }
          }, 1500); // Wait 1.5 seconds for database to be updated
        }
      } catch (error) {
        // Ignore parse errors
      }
    };

    ws.addEventListener('message', handleMessage);

    return () => {
      ws.removeEventListener('message', handleMessage);
    };
    // Deliberate: the load* helpers are rebuilt on every render, so listing
    // them here would fetch -> setState -> render -> fetch forever.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws, isConnected, selectedApplication, loadTestRuns]);

  const handleViewDetails = (run) => {
    setSelectedRun(run);
    setShowDetailsModal(true);
  };

  const handleRerun = (run) => {
    // Ask before starting. A re-run drives a real browser against the
    // environment for a minute or more, so it should not begin on a stray click.
    const label = run.test_name || run.testSuite || 'this test';
    setConfirmModal({
      isOpen: true,
      message: `Re-run "${label}"? It will run against the live environment and appear in this list when it finishes.`,
      onConfirm: () => {
        setConfirmModal({ isOpen: false, message: '', onConfirm: null });
        performRerun(run);
      }
    });
  };

  const performRerun = async (run) => {
    // Re-runs through the same backend path the Scheduling page's Run uses, so
    // there is one way tests are executed rather than two that can drift apart.
    const runId = run.id || run.dbId;
    if (!runId) return;

    setRerunningIds(prev => new Set(prev).add(runId));
    try {
      const response = await fetch(`${API_BASE_URL}/api/test-runs/${runId}/rerun`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      const data = await response.json();

      if (data.success) {
        // No dialog on success. A modal with an OK button reads as "click to
        // start", but the run has already begun by the time it appears - so it
        // asked for permission it had not waited for. The row shows the state
        // instead, the way the Scheduling page does.
        //
        // Follow the run to completion. This used to refresh on a timer and
        // give up after 90 seconds, so a longer suite finished with the row
        // still looking idle.
        waitForExecution(data.executionId, {
          onTick: () => loadTestRuns()
        }).then(result => {
          loadTestRuns();
          setRerunningIds(prev => { const next = new Set(prev); next.delete(runId); return next; });
          if (result.status === 'error') {
            setAlertModal({ isOpen: true, type: 'error', message: `The run failed to start: ${result.error}` });
          } else if (result.timedOut) {
            setAlertModal({ isOpen: true, type: 'info', message: 'Still running after 30 minutes - check the Test Runs list.' });
          }
        });
        return; // keep the row marked as running
      }

      setAlertModal({ isOpen: true, type: 'error', message: data.error || 'Could not start the run' });
      setRerunningIds(prev => { const next = new Set(prev); next.delete(runId); return next; });
    } catch (error) {
      setAlertModal({ isOpen: true, type: 'error', message: `Could not start the run: ${error.message}` });
      setRerunningIds(prev => { const next = new Set(prev); next.delete(runId); return next; });
    }
  };

  const handleStop = (run) => {
    // TODO: Implement stop functionality
    console.log('Stopping:', run);
  };

  // Ask the server why a run failed. The endpoint answers { available: false }
  // when no AI key is configured, so this button is harmless on a server
  // without one - it explains that instead of erroring.
  const handleExplain = async (run) => {
    setExplainModal({ isOpen: true, run, loading: true, text: '', reason: '' });
    try {
      const response = await fetch(`${API_BASE_URL}/api/test-runs/${run.id}/explain`);
      const data = await response.json();
      setExplainModal({
        isOpen: true,
        run,
        loading: false,
        text: data.available ? data.explanation : '',
        reason: data.available ? '' : (data.reason || 'No explanation is available for this run.'),
      });
    } catch (error) {
      setExplainModal({
        isOpen: true, run, loading: false, text: '',
        reason: 'Could not reach the server. The trace and log are still available.',
      });
    }
  };

  const handleViewTrace = async (run) => {
    console.log('handleViewTrace called for run:', run);
    console.log('trace_file_path:', run.trace_file_path);
    
      if (!run.trace_file_path) {
        setAlertModal({ isOpen: true, message: 'No trace file available for this test run', type: 'warning' });
        return;
      }
    
    try {
      console.log('Calling API to open trace viewer for run ID:', run.id);
      const response = await fetch(`${API_BASE_URL}/api/test-runs/${run.id}/view-trace`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      console.log('API response:', data);
      
      if (data.success) {
        if (data.mode === 'viewer') {
          // Running on a server: there is no screen for Playwright to open a
          // viewer window on, and it would be the SERVER's screen anyway. Open
          // the viewer as a WEB PAGE in the tester's own browser instead - it
          // loads the trace from the signed link, so there is nothing to
          // download or drag in by hand.
          window.open(data.viewerUrl, '_blank', 'noopener,noreferrer');
        } else {
          setAlertModal({ isOpen: true, message: 'Trace viewer is opening in a new window. Please check your browser.', type: 'info' });
          console.log('Trace viewer opened:', data.tracePath);
        }
      } else {
        setAlertModal({ isOpen: true, message: `Error opening trace: ${data.error || 'Unknown error'}`, type: 'error' });
      }
    } catch (error) {
      console.error('Error opening trace:', error);
      setAlertModal({ isOpen: true, message: `Error opening trace: ${error.message}`, type: 'error' });
    }
  };

  const normalizeDate = (dateStr) => {
    if (!dateStr) return null;
    // If backend timestamp has no timezone (e.g., "2025-02-06 10:00:00"), treat it as UTC to avoid TZ shifts
    const lacksTimezone = typeof dateStr === 'string' && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(dateStr);
    const normalized = lacksTimezone ? dateStr.replace(' ', 'T') + 'Z' : dateStr;
    return new Date(normalized);
  };

  const formatTimeAgo = (dateStr) => {
    const date = normalizeDate(dateStr);
    if (!date || isNaN(date.getTime())) return 'N/A';
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'min' : 'mins'} ago`;
    if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    return `${Math.floor(diffHours / 24)} days ago`;
  };

  const formatDuration = (ms) => {
    if (!ms) return '-';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    }
    return `${seconds}s`;
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'passed':
      case 'success':
        return { class: 'status-badge passed', dot: '●', text: 'Passed' };
      case 'failed':
      case 'failure':
        return { class: 'status-badge failed', dot: '●', text: 'Failed' };
      case 'running':
      case 'in_progress':
        return { class: 'status-badge running', dot: '●', text: 'Running' };
      default:
        return { class: 'status-badge unknown', dot: '○', text: status || 'Unknown' };
    }
  };

  const getBrowserIcon = (env) => {
    if (env?.toLowerCase().includes('chrome')) {
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <circle cx="8" cy="8" r="7" fill="#4285F4"/>
          <path d="M8 1C10.5 1 12.5 2.5 13.5 4.5L8 8V1Z" fill="#EA4335"/>
          <path d="M1 8C1 5.5 2.5 3.5 4.5 2.5L8 8H1Z" fill="#FBBC04"/>
          <path d="M1 8C1 10.5 2.5 12.5 4.5 13.5L8 8H1Z" fill="#34A853"/>
        </svg>
      );
    } else if (env?.toLowerCase().includes('firefox')) {
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M8 1C4.5 1 1.5 4 1.5 7.5C1.5 11 4.5 14 8 14C11.5 14 14.5 11 14.5 7.5C14.5 4 11.5 1 8 1Z" fill="#FF7139"/>
        </svg>
      );
    } else if (env?.toLowerCase().includes('safari')) {
      return (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M8 1L14 8L8 15L2 8L8 1Z" fill="#000000"/>
        </svg>
      );
    }
    return null;
  };

  return (
    <div className="test-runs-container">
      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-icon blue">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M10 2C12.5 2 14.5 4 14.5 6.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="summary-content">
            <div className="summary-value">{summaryStats.totalRuns}</div>
            <div className="summary-label">Total Runs</div>
            <div className="summary-subtext">Last 30 days</div>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon green">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M16 6L8 14L4 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div className="summary-content">
            <div className="summary-value">{summaryStats.successRate}%</div>
            <div className="summary-label">Success Rate</div>
            <div className="summary-subtext">{summaryStats.passed} passed, {summaryStats.failed} failed</div>
          </div>
        </div>

        <div className="summary-card">
          <div className="summary-icon purple">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5"/>
              <path d="M10 4V10L14 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
          <div className="summary-content">
            <div className="summary-value">{summaryStats.avgDuration}s</div>
            <div className="summary-label">Avg Duration</div>
            <div className="summary-subtext">Per test execution</div>
          </div>
        </div>
      </div>

      {/* Table Section */}
      <div className="test-runs-section">
        <div className="section-header">
          <div>
            <h2 className="section-title">Test Runs</h2>
            <p className="section-subtitle">View execution history and results</p>
          </div>
          <div className="table-filters">
            <CustomDropdown
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              options={[
                { value: 'All Status', label: 'All Status' },
                { value: 'Passed', label: 'Passed' },
                { value: 'Failed', label: 'Failed' },
                { value: 'Running', label: 'Running' }
              ]}
              className="filter-select"
            />
            {/* <CustomDropdown
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value)}
              options={[
                { value: 'Last 7 days', label: 'Last 7 days' },
                { value: 'Last 30 days', label: 'Last 30 days' },
                { value: 'Last 90 days', label: 'Last 90 days' },
                { value: 'All time', label: 'All time' }
              ]}
              className="filter-select"
            /> */}
              {/* <button className="btn-export">Export</button> */}
          </div>
        </div>

        <div className="test-runs-table-container">
          {loading ? (
            <>
              <div className="test-runs-table-wrapper">
                <table className="test-runs-table">
                  <thead>
                    <tr>
                      <th>RUN ID</th>
                      <th>TEST SUITE</th>
                      <th>STATUS</th>
                      <th>ENVIRONMENT</th>
                      <th>DURATION</th>
                      <th>PASSED/TOTAL</th>
                      <th>STARTED</th>
                      <th>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: 10 }).map((_, index) => (
                      <ShimmerTableRow key={`shimmer-${index}`} columns={8} />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : testRuns.length === 0 ? (
            <>
              <div className="empty-state">
                <p>No test runs found.</p>
              </div>
            </>
          ) : (
            <>
              <div className="test-runs-table-wrapper">
                <table className="test-runs-table">
                  <thead>
                    <tr>
                      <th>RUN ID</th>
                      <th>TEST SUITE</th>
                      <th>STATUS</th>
                      <th>ENVIRONMENT</th>
                      <th>DURATION</th>
                      <th>PASSED/TOTAL</th>
                      <th>STARTED</th>
                      <th>ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {testRuns.map((run) => {
                      const statusBadge = getStatusBadge(run.status);
                      // Debug: log trace_file_path for each run
                      if (run.trace_file_path) {
                        console.log(`Run ${run.id} has trace_file_path:`, run.trace_file_path);
                      }
                      return (
                        <tr key={run.id}>
                          <td>
                            <span className="run-id">#{run.run_id || `RUN-${run.id}`}</span>
                          </td>
                          <td>
                            <span className="suite-name">{run.test_suite_name || run.test_name || 'N/A'}</span>
                          </td>
                          <td>
                            <span className={statusBadge.class}>
                              {statusBadge.dot} {statusBadge.text}
                            </span>
                          </td>
                          <td>
                            <div className="environment-cell">
                              {getBrowserIcon(run.environment)}
                              <span>{run.environment || 'Chrome 114'}</span>
                            </div>
                          </td>
                          <td>{formatDuration(run.duration)}</td>
                          <td>
                            <span className="passed-total">
                              {run.passed_steps || 0}/{run.total_steps || 0}
                            </span>
                          </td>
                          <td>{formatTimeAgo(run.started_at)}</td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="action-btn"
                                onClick={() => handleViewDetails(run)}
                                title="View Details"
                              >
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                  <path d="M1 8C1 8 3 4 8 4C13 4 15 8 15 8C15 8 13 12 8 12C3 12 1 8 1 8Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  <circle cx="8" cy="8" r="2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </button>
                              {run.status !== 'passed' && (
                                <button
                                  className="action-btn"
                                  onClick={() => handleExplain(run)}
                                  title="Why did this fail?"
                                  style={{ color: '#0aa197' }}
                                >
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.5"/>
                                    <path d="M6.2 6.1a1.8 1.8 0 1 1 2.4 1.7c-.4.15-.6.5-.6.9v.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                    <circle cx="8" cy="11.4" r=".85" fill="currentColor"/>
                                  </svg>
                                </button>
                              )}
                              {run.trace_file_path ? (
                                <button
                                  className="action-btn"
                                  onClick={() => handleViewTrace(run)}
                                  title="View Trace"
                                  style={{ color: '#8B5CF6' }}
                                >
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M2 4L8 1L14 4V12L8 15L2 12V4Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M8 1V8M8 8L2 4M8 8L14 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </button>
                              ) : (
                                <button
                                  className="action-btn"
                                  disabled
                                  title="No trace available"
                                  style={{ opacity: 0.3, cursor: 'not-allowed' }}
                                >
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M2 4L8 1L14 4V12L8 15L2 12V4Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M8 1V8M8 8L2 4M8 8L14 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </button>
                              )}
                              {run.status === 'running' ? (
                                <button
                                  className="action-btn"
                                  onClick={() => handleStop(run)}
                                  title="Stop"
                                >
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <rect x="4" y="4" width="8" height="8" rx="1" fill="currentColor"/>
                                  </svg>
                                </button>
                              ) : (
                                <button
                                  className="action-btn"
                                  onClick={() => handleRerun(run)}
                                  disabled={rerunningIds.has(run.id || run.dbId)}
                                  title={rerunningIds.has(run.id || run.dbId) ? 'Starting...' : 'Rerun'}
                                >
                                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M13.5 2.5C12.5 1.5 11 1 9.5 1C6.5 1 4 3.5 4 6.5C4 9.5 6.5 12 9.5 12C11 12 12.5 11.5 13.5 10.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                    <path d="M12 2.5L13.5 1L15 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                  </svg>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              
              {pagination.total > 10 && (
                <div className="pagination">
                  <span className="pagination-info">
                    Showing {testRuns.length > 0 ? ((currentPage - 1) * pagination.limit) + 1 : 0} - {Math.min(currentPage * pagination.limit, pagination.total)} of {pagination.total} runs
                  </span>
                  {pagination.totalPages > 1 ? (
                    <div className="pagination-controls">
                      <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        &lt;
                      </button>
                      {Array.from({ length: Math.min(3, pagination.totalPages) }, (_, i) => {
                        const page = i + 1;
                        return (
                          <button
                            key={page}
                            className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                            onClick={() => setCurrentPage(page)}
                          >
                            {page}
                          </button>
                        );
                      })}
                      <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === pagination.totalPages}
                      >
                        &gt;
                      </button>
                    </div>
                  ) : (
                    <div className="pagination-controls"></div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Why did this fail? - AI explanation */}
      {explainModal.isOpen && (
        <div className="modal-overlay" onClick={() => setExplainModal({ isOpen: false, run: null, loading: false, text: '', reason: '' })}>
          <div className="modal-content details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Why did this fail?</h2>
              <button className="modal-close" onClick={() => setExplainModal({ isOpen: false, run: null, loading: false, text: '', reason: '' })}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h3>{explainModal.run?.test_name || 'Test run'}</h3>

                {explainModal.loading && (
                  <p style={{ color: '#6b7280' }}>Reading the error and the page&rsquo;s state&hellip;</p>
                )}

                {!explainModal.loading && explainModal.text && (
                  <pre style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'inherit',
                    fontSize: '0.95rem',
                    lineHeight: 1.6,
                    margin: 0,
                    padding: '14px 16px',
                    background: '#f6faf9',
                    border: '1px solid #d8e6e4',
                    borderLeft: '3px solid #0aa197',
                    borderRadius: '6px',
                    color: '#1f2937',
                  }}>{explainModal.text}</pre>
                )}

                {!explainModal.loading && !explainModal.text && (
                  <p style={{ color: '#6b7280' }}>{explainModal.reason}</p>
                )}

                {!explainModal.loading && explainModal.text && (
                  <p style={{ marginTop: '12px', fontSize: '0.8rem', color: '#9ca3af' }}>
                    Written by AI from the error and the page as it was when the test stopped. Check it against the trace before acting on it.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedRun && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-content details-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Test Run Details</h2>
              <button className="modal-close" onClick={() => setShowDetailsModal(false)}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h3>Test Information</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Run ID:</label>
                    <span>#{selectedRun.run_id || `RUN-${selectedRun.id}`}</span>
                  </div>
                  <div className="detail-item">
                    <label>Test Suite:</label>
                    <span>{selectedRun.test_suite_name || selectedRun.test_name || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Status:</label>
                    <span className={getStatusBadge(selectedRun.status).class}>
                      {getStatusBadge(selectedRun.status).text}
                    </span>
                  </div>
                  <div className="detail-item">
                    <label>Environment:</label>
                    <span>{selectedRun.environment || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Duration:</label>
                    <span>{formatDuration(selectedRun.duration)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Started At:</label>
                    <span>{(() => {
                      const d = normalizeDate(selectedRun.started_at);
                      return d && !isNaN(d.getTime()) ? d.toLocaleString() : 'N/A';
                    })()}</span>
                  </div>
                  {selectedRun.total_steps > 0 && (
                    <>
                      <div className="detail-item">
                        <label>Total Steps:</label>
                        <span>{selectedRun.total_steps}</span>
                      </div>
                      <div className="detail-item">
                        <label>Passed:</label>
                        <span className="text-success">{selectedRun.passed_steps || 0}</span>
                      </div>
                      <div className="detail-item">
                        <label>Failed:</label>
                        <span className="text-error">{selectedRun.failed_steps || 0}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
              
              {selectedRun.error_message && (
                <div className="detail-section">
                  <h3>Error Message</h3>
                  <div className="error-box">{selectedRun.error_message}</div>
                </div>
              )}
              
              {selectedRun.output && (
                <div className="detail-section">
                  <h3>Output</h3>
                  <pre className="output-box">{selectedRun.output}</pre>
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setShowDetailsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        message={confirmModal.message}
        title="Re-run test"
        confirmText="Run"
        cancelText="Cancel"
        type="warning"
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ isOpen: false, message: '', onConfirm: null })}
      />

      <AlertModal
        isOpen={alertModal.isOpen}
        message={alertModal.message}
        type={alertModal.type}
        onClose={() => setAlertModal({ isOpen: false, message: '', type: 'info' })}
      />
    </div>
  );
}

export default TestRuns;
